import React, { useState, useEffect, useCallback } from 'react';
import { H5 } from '../../../../AbstractElements';
import { FormGroup, Row, Col, Input, Button, ListGroup, ListGroupItem, Card, Label, Spinner } from 'reactstrap';
import { PlusCircle, Trash2, Download } from 'react-feather';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { fetchFormData, uploadFile, deleteFile } from '../../../../Api/ApiMethods';

// FilesComponent - Gelişmiş Dosya Yönetimi Bileşeni
const FilesComponent = ({
    onFilesChange,
    existingFiles = [],
    multiple = true,
    canEdit = true,
    callFormReference = false, // true: çağrı formu için, false: diğer objeler için
    referenceId, // callFormReference=true ise callFormId, false ise objeId
    typeFilter = null, // Belirli dosya türlerini title'a göre filtrelemek için array (örn: ['VEKALETNAME', 'FATURA'])
    excludeTypes = false, // true ise typeFilter'daki türleri hariç tut, false ise sadece o türleri göster
    title = "Belgeler" // Başlık özelleştirilebilir
}) => {
    console.log('[DEBUG] FilesComponent mounted/re-rendered with props:', { referenceId, typeFilter, excludeTypes, title });
    
    // State tanımlamaları
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [fileTypes, setFileTypes] = useState([]);
    const [currentFile, setCurrentFile] = useState(null);
    const [currentFileTypeId, setCurrentFileTypeId] = useState('');
    const [isAddingFile, setIsAddingFile] = useState(false);
    const [loadingTypes, setLoadingTypes] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [deletingFileId, setDeletingFileId] = useState(null);

    // Dosya türlerini API'den çek
    const fetchFileTypes = useCallback(async () => {
        console.log('[DEBUG] FilesComponent fetchFileTypes called');
        setLoadingTypes(true);
        try {
            console.log('[DEBUG] FilesComponent fetching file types from /FileType');
            const data = await fetchFormData({
                apiEndpoint: '/FileType',
                page: 1,
                pageSize: 1000, // Tüm türleri çek
                search: ''
            });

            console.log('[DEBUG] FilesComponent file types API response:', data);
            if (data && Array.isArray(data.items)) {
                let types = data.items;

                // typeFilter varsa title'a göre filtrele
                if (typeFilter && Array.isArray(typeFilter) && typeFilter.length > 0) {
                    if (excludeTypes) {
                        // excludeTypes true ise, typeFilter'daki türleri hariç tut
                        types = types.filter(type => !typeFilter.includes(type.title));
                    } else {
                        // excludeTypes false ise, sadece typeFilter'daki türleri göster
                        types = types.filter(type => typeFilter.includes(type.title));
                    }
                }

                setFileTypes(types);
            } else {
                setFileTypes([]);
                console.warn('FileTypes API response does not contain items array');
            }
        } catch (error) {
            console.error('Error fetching file types:', error);
            toast.error('Dosya türleri yüklenirken hata oluştu.');
            setFileTypes([]);
        } finally {
            setLoadingTypes(false);
        }
    }, [JSON.stringify(typeFilter), excludeTypes]);    // typeFilter'ı JSON.stringify ile stabilize et, excludeTypes ekle

    // Track component mounting
    useEffect(() => {
        console.log('[DEBUG] FilesComponent mounted');
        return () => {
            console.log('[DEBUG] FilesComponent unmounted');
        };
    }, []);

    // Component mount edildiğinde dosya türlerini çek
    useEffect(() => {
        fetchFileTypes();
    }, [fetchFileTypes]);

    // referenceId değiştiğinde mevcut dosyaları çek  
    useEffect(() => {
        const fetchFiles = async () => {
            if (!referenceId) {
                setUploadedFiles([]); // referenceId yoksa dosyaları temizle
                return;
            }

            // Eğer typeFilter var ve excludeTypes=false ise (server-side filtreleme yapacaksak)
            // fileTypes'ın yüklenmesini bekle
            if (typeFilter && Array.isArray(typeFilter) && typeFilter.length > 0 && !excludeTypes && fileTypes.length === 0) {
                console.log('[DEBUG] FilesComponent waiting for fileTypes to load for server-side filtering');
                return; // fileTypes henüz yüklenmemiş, bekle
            }

            try {
                const params = {
                    page: 1,
                    pageSize: 50,
                    search: ''
                };

                // Çağrı formu veya diğer objeler için farklı parametre ekleme
                if (callFormReference) {
                    params.callFormReference = referenceId;
                } else {
                    params.reference = referenceId;
                }

                // typeFilter varsa ve fileTypes yüklenmişse, fileType ID'lerini parametre olarak ekle
                // Ancak sadece excludeTypes=false ise (yani sadece belirli türleri göstermek istiyorsak)
                if (typeFilter && Array.isArray(typeFilter) && typeFilter.length > 0 && fileTypes.length > 0 && !excludeTypes) {
                    const filteredTypeIds = fileTypes
                        .filter(type => typeFilter.includes(type.title))
                        .map(type => type.id);
                    
                    if (filteredTypeIds.length > 0) {
                        // Tek fileType için filtreleme (sadece excludeTypes=false iken)
                        if (filteredTypeIds.length === 1) {
                            params.fileType = filteredTypeIds[0];
                        }
                    }
                }            console.log('[DEBUG] FilesComponent making API call to /UploadedFile with params:', params);
            const data = await fetchFormData({
                apiEndpoint: '/UploadedFile',
                ...params
            });

                if (data && Array.isArray(data.items)) {
                    console.log('FilesComponent DEBUG - API Response:', {
                        callFormReference,
                        referenceId,
                        typeFilter,
                        excludeTypes,
                        totalItems: data.items.length,
                        items: data.items.map(item => ({
                            fileName: item.fileName,
                            typeTitle: item.type?.title
                        }))
                    });

                    let files = data.items.map((item, index) => {
                        // Type bilgisini çeşitli kaynaklardan al
                        let typeInfo = null;
                        let typeTitle = null;

                        if (item.type) {
                            typeInfo = item.type;
                            typeTitle = typeof item.type === 'object' ? item.type.title : null;
                        } else if (item.fileType) {
                            typeInfo = item.fileType.id || item.fileType;
                            typeTitle = item.fileType.title;
                        } else if (item.typeId) {
                            typeInfo = item.typeId;
                        }

                        return {
                            id: item.id || Date.now() + index,
                            file: item, // Sunucudan gelen dosya bilgisi
                            type: typeInfo, // API'den gelen type objesi veya ID
                            typeTitle: typeTitle, // Type'ın title'ı
                            fileName: item.fileName || item.name || 'Bilinmeyen Dosya',
                            isExisting: true,
                            serverResponse: item
                        };
                    });

                    // Client-side filtreleme: typeFilter varsa, excludeTypes'a göre filtrele
                    if (typeFilter && Array.isArray(typeFilter) && typeFilter.length > 0) {
                        const originalLength = files.length;
                        files = files.filter(file => {
                            const fileTypeTitle = file.typeTitle;
                            const matches = fileTypeTitle && typeFilter.includes(fileTypeTitle);
                            
                            // excludeTypes true ise, typeFilter'daki türleri hariç tut (tersini döndür)
                            // excludeTypes false ise, sadece typeFilter'daki türleri göster
                            return excludeTypes ? !matches : matches;
                        });
                        
                        // Debug log
                        if (originalLength !== files.length) {
                            console.log(`FilesComponent filtered files for typeFilter [${typeFilter.join(', ')}] excludeTypes=${excludeTypes}: ${originalLength} -> ${files.length}`);
                        }
                    }

                    setUploadedFiles(multiple ? files : files.slice(0, 1));
                } else {
                    setUploadedFiles([]);
                }
            } catch (error) {
                console.error('Error fetching existing files:', error);
                // Hata durumunda sessizce devam et, kullanıcıya error gösterme
                setUploadedFiles([]);
            }
        };

        fetchFiles();
    }, [referenceId, callFormReference, multiple, fileTypes]); // fileTypes dependency eklendi

    // existingFiles prop'u varsa manuel olarak eklenen dosyalarla birleştir (opsiyonel backward compatibility)
    useEffect(() => {
        if (existingFiles && existingFiles.length > 0 && !referenceId) {
            // Sadece referenceId yoksa existingFiles'ı kullan (fallback)
            const initialFiles = existingFiles.map((f, index) => ({
                id: f.id || Date.now() + index,
                file: f.file || f,
                type: f.type || f.typeId,
                fileName: f.fileName || f.name || 'Bilinmeyen Dosya',
                isExisting: true
            })).filter(f => f.file);

            setUploadedFiles(multiple ? initialFiles : initialFiles.slice(0, 1));
        }
    }, [existingFiles, multiple, referenceId]);

    // Parent component'e dosya değişikliklerini bildir
    useEffect(() => {
        onFilesChange(uploadedFiles);
    }, [uploadedFiles]); // onFilesChange'i dependency'den çıkar    // Dosya türü adını ID'den bul veya type objesinden al
    const getTypeNameById = (typeIdOrObject, uploadedFile = null) => {
        // Önce uploadedFile'da tanımlı typeTitle'ı kontrol et
        if (uploadedFile && uploadedFile.typeTitle) {
            return uploadedFile.typeTitle;
        }

        // Eğer type bir obje ise (API'den gelen dosyalar için)
        if (typeof typeIdOrObject === 'object' && typeIdOrObject?.title) {
            return typeIdOrObject.title;
        }

        // Eğer sadece ID ise, fileTypes listesinden bul
        const type = fileTypes.find(ft => ft.id === typeIdOrObject);
        return type ? (type.title || type.name || 'Bilinmeyen Tür') : 'Bilinmeyen Tür';
    };// Dosya ekleme işlemini başlat
    const handleAddFileClick = () => {
        if (!referenceId) {
            if (callFormReference) {
                toast.warning('Dosya yüklemek için önce çağrı formunu kaydetmeniz gerekiyor.');
            } else {
                toast.warning('Dosya yüklemek için önce kaydı kaydetmeniz gerekiyor.');
            }
            return;
        }
        setIsAddingFile(true);
        setCurrentFile(null);
        setCurrentFileTypeId('');
    };

    // Dosya seçimi
    const handleFileSelect = (event) => {
        if (event.target.files && event.target.files[0]) {
            setCurrentFile(event.target.files[0]);
        } else {
            setCurrentFile(null);
        }
    };

    // Dosya türü seçimi
    const handleTypeSelect = (event) => {
        setCurrentFileTypeId(event.target.value);
    };

    // Dosya yükleme işlemi
    const handleConfirmAdd = async () => {
        if (!currentFile) {
            toast.warning('Lütfen bir dosya seçin.');
            return;
        }

        if (!currentFileTypeId) {
            toast.warning('Lütfen dosya türünü seçin.');
            return;
        }
        
        if (!referenceId) {
            if (callFormReference) {
                toast.warning('Referans ID bulunamadı. Önce çağrı formunu kaydetmeniz gerekiyor.');
            } else {
                toast.warning('Referans ID bulunamadı. Önce kaydı kaydetmeniz gerekiyor.');
            }
            return;
        }

        // Aynı isimde dosya kontrolü (sadece multiple=true için)
        if (multiple && uploadedFiles.some(f => f.fileName === currentFile.name)) {
            toast.warning(`"${currentFile.name}" adlı dosya zaten eklenmiş.`);
            return;
        }

        setUploadingFile(true);

        try {
            // UploadFile API'sine dosya yükle
            const uploadData = {
                apiEndpoint: '/UploadedFile',
                fileName: currentFile.name,
                fileTypeId: currentFileTypeId,
                file: currentFile,
                reference: callFormReference ? null : referenceId, // callForm değilse reference'a yaz
                callFormReference: callFormReference ? referenceId : null // callForm ise callFormReference'a yaz
            };

            const response = await uploadFile(uploadData);

            if (response && response.isSuccess) {
                // Başarılı yükleme - listeye ekle
                const newFileData = {
                    id: response.data?.id || Date.now(),
                    file: currentFile,
                    type: currentFileTypeId,
                    fileName: currentFile.name,
                    isExisting: false,
                    serverResponse: response.data
                };

                setUploadedFiles(prev => multiple ? [...prev, newFileData] : [newFileData]);

                toast.success('Dosya başarıyla yüklendi.');

                // Form'u temizle
                setIsAddingFile(false);
                setCurrentFile(null);
                setCurrentFileTypeId('');
            } else {
                toast.error('Dosya yüklenemedi.');
            }
        } catch (error) {
            console.error('File upload error:', error);
            toast.error('Dosya yüklenirken hata oluştu.');
        } finally {
            setUploadingFile(false);
        }
    };

    // Dosya ekleme işlemini iptal et
    const handleCancelAdd = () => {
        setIsAddingFile(false);
        setCurrentFile(null);
        setCurrentFileTypeId('');
    };    // Dosya silme işlemi
    const handleRemoveFile = async (fileIdToRemove, fileData) => {
        // Onay diyalogu
        const isExisting = fileData.isExisting;
        const confirmMessage = isExisting
            ? `"${getFileName(fileData)}" dosyasını sunucudan kalıcı olarak silmek istediğinizden emin misiniz?`
            : `"${getFileName(fileData)}" dosyasını listeden çıkarmak istediğinizden emin misiniz?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setDeletingFileId(fileIdToRemove);

        try {
            // Eğer sunucuda bulunan bir dosya ise API'den sil
            if (isExisting && fileData.serverResponse && fileData.serverResponse.id) {
                await deleteFile(fileData.serverResponse.id);
                toast.success('Dosya başarıyla silindi.');
            } else {
                toast.success('Dosya listeden çıkarıldı.');
            }

            // Her durumda listeden çıkar
            setUploadedFiles(prevFiles => prevFiles.filter(f => f.id !== fileIdToRemove));
        } catch (error) {
            console.error('Error removing file:', error);
            // Hata durumunda kullanıcıya bildirme, API silme başarısız oldu
            toast.error('Dosya silinemedi. Lütfen tekrar deneyin.');
        } finally {
            setDeletingFileId(null);
        }
    };

    // Dosya adını al
    const getFileName = (fileData) => {
        if (fileData.fileName) return fileData.fileName;
        if (fileData.file instanceof File) return fileData.file.name;
        if (typeof fileData.file === 'object' && fileData.file?.name) return fileData.file.name;
        return 'Bilinmeyen Dosya';
    };    // Dosya indirme (mevcut dosyalar için)
    const handleDownloadFile = (fileData) => {
        if (fileData.isExisting && fileData.serverResponse) {
            // Dosyanın file array'indeki ilk ID'yi al
            const fileId = fileData.serverResponse.file && fileData.serverResponse.file.length > 0
                ? fileData.serverResponse.file[0]
                : fileData.serverResponse.id;

            // FILE_API_PREVIEW_ENDPOINT kullanarak indirme URL'i oluştur
            const downloadUrl = `${process.env.REACT_APP_FILE_API_PREVIEW_ENDPOINT}/${fileId}`;

            // Yeni sekmede aç
            window.open(downloadUrl, '_blank');
        } else {
            toast.warning('Bu dosya henüz sunucuya yüklenmemiş.');
        }
    };

    // Layout hesaplamaları
    const fileInputColSize = 5;
    const typeSelectColSize = 4;
    const buttonColSize = 3;

    return (
        <>
            <H5>{title}</H5>

            {/* Dosya ekleme butonu */}
            {canEdit && !isAddingFile && (multiple || uploadedFiles.length === 0) && (
                <Button
                    color="primary"
                    outline
                    onClick={handleAddFileClick}
                    className="mb-3 d-flex align-items-center gap-2"
                    disabled={!referenceId}
                >
                    <PlusCircle size={18} />
                    <span className='mb-0'>Dosya Ekle</span>
                </Button>
            )}            {!referenceId && (
                <p className="text-warning mb-3">
                    <small>
                        {callFormReference
                            ? 'Dosya yüklemek için önce çağrı formunu kaydetmeniz gerekiyor.'
                            : 'Dosya yüklemek için önce kaydı kaydetmeniz gerekiyor.'
                        }
                    </small>
                </p>
            )}

            {/* Dosya ekleme formu */}
            {canEdit && isAddingFile && (
                <Card className="mb-3 p-3 border">
                    <Row className="g-2 align-items-end">
                        {/* Dosya seçimi */}
                        <Col md={fileInputColSize}>
                            <FormGroup className="m-0">
                                <Label for="fileInput">Dosya Seç</Label>
                                <Input
                                    id="fileInput"
                                    type="file"
                                    onChange={handleFileSelect}
                                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                                    disabled={uploadingFile}
                                />
                            </FormGroup>
                        </Col>

                        {/* Dosya türü seçimi */}
                        <Col md={typeSelectColSize}>
                            <FormGroup className="m-0">
                                <Label for="fileTypeSelect">
                                    Dosya Türü
                                    {loadingTypes && <Spinner size="sm" className="ms-2" />}
                                </Label>
                                <Input
                                    id="fileTypeSelect"
                                    type="select"
                                    value={currentFileTypeId}
                                    onChange={handleTypeSelect}
                                    disabled={loadingTypes || uploadingFile}
                                >
                                    <option value="">Tür Seçiniz...</option>
                                    {fileTypes.length > 0 ? (
                                        fileTypes.map(type => (
                                            <option key={type.id} value={type.id}>
                                                {type.title || type.name}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="" disabled>
                                            {loadingTypes ? 'Yükleniyor...' : 'Dosya türü bulunamadı'}
                                        </option>
                                    )}
                                </Input>
                            </FormGroup>
                        </Col>

                        {/* İşlem butonları */}
                        <Col md={buttonColSize} className="d-flex justify-content-end align-items-end">
                            <Button
                                color="success"
                                size="sm"
                                onClick={handleConfirmAdd}
                                className="me-2"
                                disabled={uploadingFile || !currentFile || !currentFileTypeId}
                            >
                                {uploadingFile ? <Spinner size="sm" /> : 'Yükle'}
                            </Button>
                            <Button
                                color="danger"
                                size="sm"
                                outline
                                onClick={handleCancelAdd}
                                disabled={uploadingFile}
                            >
                                İptal
                            </Button>
                        </Col>
                    </Row>

                    {currentFile && (
                        <small className='text-muted mt-1 d-block'>
                            Seçilen dosya: {currentFile.name} ({(currentFile.size / 1024).toFixed(2)} KB)
                        </small>
                    )}
                </Card>
            )}

            {/* Yüklenen dosyalar listesi */}
            {uploadedFiles.length > 0 && (
                <FormGroup>
                    <Label>Yüklenen Dosyalar:</Label>
                    <ListGroup>
                        {uploadedFiles.map((uploadedFile) => (
                            <ListGroupItem key={uploadedFile.id} className="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>{getFileName(uploadedFile)}</strong>
                                    <br />                                    <small className="text-muted">
                                        Tür: {getTypeNameById(uploadedFile.type, uploadedFile)}
                                        {uploadedFile.isExisting && (
                                            <span className="badge bg-info rounded-pill ms-2">Mevcut</span>
                                        )}
                                        {!uploadedFile.isExisting && (
                                            <span className="badge bg-success rounded-pill ms-2">Yeni</span>
                                        )}
                                    </small>
                                </div>

                                {/* İşlem butonları */}
                                <div className='d-flex gap-1'>
                                    {/* İndirme butonu (mevcut dosyalar için) */}
                                    {uploadedFile.isExisting && (
                                        <Button
                                            color="info"
                                            size="sm"
                                            outline
                                            onClick={() => handleDownloadFile(uploadedFile)}
                                            title="Dosyayı İndir"
                                        >
                                            <Download size={16} />
                                        </Button>
                                    )}

                                    {/* Değiştirme butonu (tek dosya modu için)
                                    {canEdit && !multiple && (
                                        <Button 
                                            color="warning" 
                                            size="sm" 
                                            outline 
                                            onClick={handleAddFileClick} 
                                            title="Dosyayı Değiştir"
                                            disabled={!referenceId}
                                        >
                                            <RefreshCw size={16} />
                                        </Button>
                                    )}                                    
                                    */}

                                    {/* Silme butonu */}
                                    {canEdit && (
                                        <Button
                                            color="danger"
                                            size="sm"
                                            outline
                                            onClick={() => handleRemoveFile(uploadedFile.id, uploadedFile)}
                                            title={uploadedFile.isExisting ? "Dosyayı Sunucudan Sil" : "Dosyayı Listeden Çıkar"}
                                            disabled={deletingFileId === uploadedFile.id}
                                        >
                                            {deletingFileId === uploadedFile.id ? <Spinner size="sm" /> : <Trash2 size={16} />}
                                        </Button>
                                    )}
                                </div>
                            </ListGroupItem>
                        ))}
                    </ListGroup>
                </FormGroup>
            )}

            {/* Dosya yoksa mesaj */}
            {uploadedFiles.length === 0 && !isAddingFile && (
                <p className="text-muted">Henüz dosya yüklenmedi.</p>
            )}
        </>
    );
};

// PropTypes tanımlamaları
FilesComponent.propTypes = {
    onFilesChange: PropTypes.func.isRequired,
    existingFiles: PropTypes.array,
    multiple: PropTypes.bool,
    canEdit: PropTypes.bool,
    callFormReference: PropTypes.bool, // true: çağrı formu, false: diğer objeler
    referenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // İlgili obje ID'si
    typeFilter: PropTypes.arrayOf(PropTypes.string), // Dosya türü filtresi (title'lara göre)
    excludeTypes: PropTypes.bool, // true ise typeFilter'daki türleri hariç tut
    title: PropTypes.string // Başlık
};

export default FilesComponent;
